/** AUTH-01 Connexion · AUTH-02 Vérification MFA · AUTH-03 Enrôlement MFA forcé (internes). */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Button, Card, TextField } from '@myron/design-system';
import { ApiErreur, messageErreur, post } from '../../shared/api/client';
import { useSession, type Session } from '../../shared/auth/session';
import { MentionSimule } from '../../shared/ui/composants';

const schema = z.object({ email: z.string().email('Adresse e-mail invalide'), motDePasse: z.string().min(1, 'Mot de passe requis') });

/** Destination après connexion : `retour` s'il est interne au site, sinon l'espace du compte. */
function destination(s: Session, retour: string | null) {
  if (retour && retour.startsWith('/') && !retour.startsWith('//')) {
    const interne = s.utilisateur.type === 'INTERNE';
    if ((interne && retour.startsWith('/admin')) || (!interne && retour.startsWith('/pro')) || (!retour.startsWith('/admin') && !retour.startsWith('/pro'))) return retour;
  }
  return s.utilisateur.type === 'INTERNE' ? '/admin' : '/pro';
}

export function PageConnexion() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { definir } = useSession();
  const [erreur, setErreur] = useState<unknown>(null);
  const f = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { email: '', motDePasse: '' } });
  const retour = params.get('retour');

  const soumettre = f.handleSubmit(async (v) => {
    setErreur(null);
    try {
      const r = await post<{ etape: 'CONNECTE' | 'MFA_REQUISE' | 'ENROLEMENT_MFA_REQUIS'; session: Session }>('/auth/connexion', v);
      definir({ ...r.session, etape: r.etape });
      const q = retour ? `?retour=${encodeURIComponent(retour)}` : '';
      if (r.etape === 'MFA_REQUISE') nav(`/connexion/mfa${q}`);
      else if (r.etape === 'ENROLEMENT_MFA_REQUIS') nav(`/connexion/enrolement-mfa${q}`);
      else nav(destination(r.session, retour));
    } catch (e) {
      setErreur(e);
    }
  });

  return (
    <Card as="section" aria-labelledby="titre-connexion">
      <div className="my-pile">
        <h1 id="titre-connexion" className="my-titre-2">Connexion</h1>
        {params.get('expiree') && <Alert ton="alerte" titre="Session expirée">Votre session a expiré après une période d'inactivité. Reconnectez-vous pour continuer.</Alert>}
        {params.get('deconnecte') && <Alert ton="succes">Vous êtes déconnecté.</Alert>}
        {params.get('reinitialise') && <Alert ton="succes">Mot de passe modifié. Connectez-vous avec votre nouveau mot de passe.</Alert>}
        {erreur instanceof ApiErreur && erreur.code === 'LIMITE_TENTATIVES' ? (
          <Alert ton="erreur" titre="Compte temporairement verrouillé">{erreur.message} {erreur.probleme?.reessayerDans ? `(environ ${Math.ceil(Number(erreur.probleme.reessayerDans) / 60)} min)` : ''}</Alert>
        ) : erreur ? (
          <Alert ton="erreur" titre="Connexion impossible">{messageErreur(erreur)}</Alert>
        ) : null}
        <form className="my-form" onSubmit={soumettre} noValidate>
          <TextField label="Adresse e-mail professionnelle" type="email" autoComplete="username" required erreur={f.formState.errors.email?.message} {...f.register('email')} />
          <TextField label="Mot de passe" type="password" autoComplete="current-password" required erreur={f.formState.errors.motDePasse?.message} {...f.register('motDePasse')} />
          <Button type="submit" chargement={f.formState.isSubmitting}>Se connecter</Button>
        </form>
        <div className="my-ligne my-ligne--espace my-petit">
          <Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>
          <Link to="/devenir-client">Créer un compte professionnel</Link>
        </div>
        <p className="my-legende my-secondaire">Comptes de démonstration : panneau « Simulation » en haut de page. <MentionSimule /></p>
      </div>
    </Card>
  );
}

export function PageVerificationMfa() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { definir, session } = useSession();
  const [code, setCode] = useState('');
  const [recuperation, setRecuperation] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const [bloque, setBloque] = useState(false);

  const valider = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      const s = await post<Session>('/auth/mfa/verifier', recuperation ? { codeRecuperation: code.trim() } : { code: code.trim() });
      definir({ ...s, etape: 'CONNECTE' });
      nav(destination(s, params.get('retour')));
    } catch (er) {
      setErreur(er);
      if (er instanceof ApiErreur && er.code === 'LIMITE_TENTATIVES') setBloque(true);
    } finally {
      setEnvoi(false);
    }
  };

  if (bloque) {
    return <Alert ton="erreur" titre="Vérification bloquée" action={<Link to="/connexion" onClick={() => definir(null)}>Revenir à la connexion</Link>}>{messageErreur(erreur)}</Alert>;
  }
  if (!session) {
    return <Alert ton="alerte" titre="Session de connexion absente" action={<Link to="/connexion">Revenir à la connexion</Link>}>Recommencez la connexion.</Alert>;
  }
  return (
    <Card as="section" aria-labelledby="titre-mfa">
      <form className="my-form" onSubmit={valider}>
        <h1 id="titre-mfa" className="my-titre-2">Vérification en deux étapes</h1>
        <p className="my-secondaire">
          {recuperation ? 'Saisissez un de vos codes de récupération (format XXXX-XXXX). Chaque code ne sert qu’une fois.' : 'Saisissez le code à 6 chiffres affiché par votre application d’authentification.'}
        </p>
        {erreur ? <Alert ton="erreur" titre="Vérification refusée">{messageErreur(erreur)}</Alert> : null}
        <TextField
          label={recuperation ? 'Code de récupération' : 'Code à 6 chiffres'}
          inputMode={recuperation ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          pattern={recuperation ? undefined : '[0-9]{6}'}
          maxLength={recuperation ? 9 : 6}
        />
        <Button type="submit" chargement={envoi} disabled={!code.trim()}>Vérifier</Button>
        <Button variante="tertiaire" onClick={() => { setRecuperation((r) => !r); setCode(''); setErreur(null); }}>
          {recuperation ? 'Utiliser le code de l’application' : 'Utiliser un code de récupération'}
        </Button>
        <p className="my-legende my-secondaire">MFA TOTP réellement calculée sur un secret fictif lisible dans le panneau de simulation. <MentionSimule /></p>
      </form>
    </Card>
  );
}

export function PageEnrolementMfa() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { definir, session } = useSession();
  const [secret, setSecret] = useState<{ secretBase32: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);

  if (!session) return <Alert ton="alerte" action={<Link to="/connexion">Revenir à la connexion</Link>}>Recommencez la connexion.</Alert>;

  const demarrer = async () => {
    setErreur(null);
    try { setSecret(await post('/auth/mfa/enrolement')); } catch (e) { setErreur(e); }
  };
  const confirmer = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await post<{ codes: string[] }>('/auth/mfa/enrolement/confirmer', { code: code.trim() });
      setCodes(r.codes);
    } catch (er) { setErreur(er); } finally { setEnvoi(false); }
  };
  const terminer = async () => {
    const s = await (await import('../../shared/api/client')).get<Session>('/auth/session');
    definir({ ...s, etape: 'CONNECTE' });
    nav(destination(s, params.get('retour')));
  };

  return (
    <Card as="section" aria-labelledby="titre-enrolement">
      <div className="my-pile">
        <h1 id="titre-enrolement" className="my-titre-2">Activez la vérification en deux étapes</h1>
        <Alert ton="info" titre="Obligatoire pour l’administration">
          Tout compte interne doit activer la MFA avant d’accéder à l’administration (politique MYRON ; périmètre interprété en DO-11).
        </Alert>
        {erreur ? <Alert ton="erreur" titre="Action impossible">{messageErreur(erreur)}</Alert> : null}
        {!secret && !codes && <Button onClick={demarrer}>Commencer l’activation</Button>}
        {secret && !codes && (
          <form className="my-form" onSubmit={confirmer}>
            <p>1. Ajoutez ce secret dans votre application d’authentification :</p>
            <p><code className="my-code" data-testid="secret-mfa">{secret.secretBase32}</code></p>
            <p className="my-legende my-secondaire">Secret fictif de simulation, affiché une seule fois. <MentionSimule /></p>
            <TextField label="2. Code à 6 chiffres affiché par l’application" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} required />
            <Button type="submit" chargement={envoi} disabled={code.trim().length !== 6}>Confirmer l’activation</Button>
          </form>
        )}
        {codes && (
          <div className="my-pile">
            <Alert ton="succes" titre="Vérification en deux étapes activée">Conservez ces codes de récupération en lieu sûr. Ils ne seront plus affichés.</Alert>
            <ul className="my-grille" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', listStyle: 'none', padding: 0 }} data-testid="codes-recuperation">
              {codes.map((c) => <li key={c}><code className="my-code">{c}</code></li>)}
            </ul>
            <Button onClick={terminer}>J’ai conservé mes codes — continuer</Button>
          </div>
        )}
      </div>
    </Card>
  );
}
